import { useContext, useState } from 'react';
import * as R from 'ramda';

import CreateWorkModal from '@atom/components/common/createWorkModal/CreateWorkModal';
import DeleteModal from '@atom/components/common/DeleteModal';
import HistoryDrawer from '@atom/components/common/history/HistoryDrawer';
import MoveInventoryModal from '@atom/components/common/MoveInventoryModal';
import RenameModal from '@atom/components/common/RenameModal';
import folderMoveIcon from '@atom/components/common/svgIcons/folderMoveWhite.svg';
import NewWorkOrderIcon from '@atom/components/common/svgIcons/NewWorkOrderIcon';
import textConstants from '@atom/constants/textConstants';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton, Modal, Progress, Snackbar } from '@atom/mui';
import { DoneAllIcon, EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { PolicyAction } from '@atom/types/policy';
import { hasAccess } from '@atom/utilities/accessUtilities';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isManagerInventoryMoveRoleEnabled } from '@atom/utilities/tenantFeatureUtilities';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

import AssetDetailPagination from './AssetDetailPagination';
import AssetOptionMenu from './AssetOptionMenu';
import EditAssetModal from './EditAssetModal';

import classes from './header.module.css';

const EDITABLE_ERROR_CODE = 422;

type HeaderControlsProps = {
  inventoryMoveDisabled: boolean;
};

const HeaderControls = ({ inventoryMoveDisabled }: HeaderControlsProps) => {
  const { asset, loading, updateAsset, deleteAsset, approveAllChanges } =
    useContext(InventoryDetailContext);

  const [, { navigate }] = useNavigation();
  const preferences = usePreferences();
  const requireWorkType = R.pathOr(
    false,
    ['workOrders', 'requireWorkType'],
    preferences,
  );

  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [workModalOpen, setWorkModalOpen] = useState<boolean>(false);
  const [approveAllOpen, setApproveAllOpen] = useState<boolean>(false);
  const [moveFolderModalOpen, setMoveFolderModalOpen] =
    useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [approveAllErrorOpen, setApproveAllErrorOpen] =
    useState<boolean>(false);

  const onAssetEdit = async (
    id: string,
    name: string,
    rate?: number,
    unit?: string,
    isStockBased?: boolean,
    isStartEndReading?: boolean,
    isStartEndCalculated?: boolean,
    quantityRemaining?: number,
  ) => {
    await updateAsset(
      {
        id,
        name,
        ...(asset.isMaterial && {
          budget: {
            rate,
            ...(unit && { unit }),
            ...(!isNilOrEmpty(isStockBased) && { isStockBased }),
            ...(!isNilOrEmpty(isStartEndReading) && { isStartEndReading }),
            ...(!isNilOrEmpty(isStartEndCalculated) && {
              isStartEndCalculated,
            }),
            ...(!isNilOrEmpty(quantityRemaining) && { quantityRemaining }),
          },
        }),
      },
      { includeCursor: true },
    );
  };

  const approveAllComponent = loading ? (
    <Progress progressColor={colors.neutral.white} size={25} thickness={2} />
  ) : (
    <IconButton
      edge="end"
      tooltip="Approve All Changes"
      data-cy="inventoryDetail-approve-all-button"
      onClick={() => setApproveAllOpen(true)}
    >
      <Icon color={colors.neutral.white} icon={DoneAllIcon} />
    </IconButton>
  );

  const handleApproveAll = async () => {
    setApproveAllOpen(false);

    try {
      await approveAllChanges();
    } catch (error) {
      if (getErrorCode(error) === EDITABLE_ERROR_CODE) {
        setApproveAllErrorOpen(true);
      } else {
        Snackbar.error({ message: 'Failed to approve all changes.' });
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAsset();
      navigate('/inventory');
    } catch (error) {
      if (getErrorCode(error) === EDITABLE_ERROR_CODE) {
        Snackbar.error({
          message:
            'Work is currently associated with this inventory asset. Deletion is not allowed.',
        });
      } else {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
      }
    }
  };

  const canRename = hasAccess(PolicyAction.RENAME, asset.actions);
  const canUpdate = hasAccess(PolicyAction.UPDATE, asset.actions);
  const canManageChanges = hasAccess(
    PolicyAction.MANAGE_INVENTORY_CHANGES,
    asset.actions,
  );

  const showApproveAll = canManageChanges && !!asset.changeType;
  const showEdit = asset.isMaterial && (canRename || canUpdate);
  const showRename = !asset.isMaterial && canRename;
  const showMoveInventory =
    hasRolePermissions(
      isManagerInventoryMoveRoleEnabled
        ? ROLE_SETS.MANAGER
        : ROLE_SETS.ORG_ADMIN,
    ) && !inventoryMoveDisabled;
  const showCreateWork =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !asset.archived;

  const showOptionMenu = hasRolePermissions(ROLE_SETS.MANAGER);
  const canDelete = hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  return (
    <>
      <div className={classes.iconBar}>
        {asset.cursor && <AssetDetailPagination />}
        {showApproveAll && approveAllComponent}
        {showEdit && (
          <IconButton
            edge="end"
            tooltip="Edit"
            data-cy="inventoryDetail-edit-button"
            onClick={() => setEditModalOpen(true)}
          >
            <Icon color={colors.neutral.white} icon={EditIcon} />
          </IconButton>
        )}
        {showRename && (
          <IconButton
            edge="end"
            tooltip="Rename"
            data-cy="inventoryDetail-rename-button"
            onClick={() => setRenameModalOpen(true)}
          >
            <Icon color={colors.neutral.white} icon={EditIcon} />
          </IconButton>
        )}
        <HistoryDrawer subject="assets" id={asset.id} />
        {showMoveInventory && (
          <IconButton
            edge="end"
            tooltip="Move to Folder"
            data-cy="inventoryDetail-move-to-folder-button"
            onClick={() => setMoveFolderModalOpen(true)}
          >
            <img src={folderMoveIcon} />
          </IconButton>
        )}
        {showCreateWork && (
          <IconButton
            edge="end"
            tooltip="Create Work"
            data-cy="inventoryDetail-create-work-button"
            onClick={() => setWorkModalOpen(true)}
          >
            <NewWorkOrderIcon />
          </IconButton>
        )}
        {showOptionMenu && (
          <AssetOptionMenu
            setOpenDeleteModal={setOpenDeleteModal}
            canDelete={canDelete}
          />
        )}
      </div>
      <Modal
        open={approveAllOpen}
        title="Approve All Changes"
        loading={loading}
        onConfirm={handleApproveAll}
        onCancel={() => setApproveAllOpen(false)}
        confirmButtonText="Approve All"
        data-cy="inventoryDetail-approve-all-modal"
      >
        <span>
          Are you sure you want to approve all changes to{' '}
          <strong>{asset.name}</strong>?
        </span>
      </Modal>
      <Modal
        open={approveAllErrorOpen}
        confirmButtonText="OK"
        onConfirm={() => setApproveAllErrorOpen(false)}
        ConfirmButtonProps={{
          'data-cy': 'inventoryDetailApproveAllErrorOkButton',
        }}
        title="Cannot Approve All Changes"
      >
        Some pending changes cannot be approved because the attribute is not
        editable. Please set the attribute to editable in the asset schema to
        approve or reject changes.
      </Modal>
      <RenameModal
        id={asset.id}
        name={asset.name}
        type="inventory"
        renameAction={onAssetEdit}
        open={renameModalOpen}
        closeModal={() => setRenameModalOpen(false)}
      />
      <EditAssetModal
        onAssetEdit={onAssetEdit}
        open={editModalOpen}
        closeModal={() => setEditModalOpen(false)}
      />
      <MoveInventoryModal
        id={asset.id}
        name={asset.name}
        categoryId={asset.categoryId}
        schemaId={asset.schemaId}
        open={moveFolderModalOpen}
        closeModal={() => setMoveFolderModalOpen(false)}
      />
      <CreateWorkModal
        redirectOnCreate
        open={workModalOpen}
        onClose={() => setWorkModalOpen(false)}
        asset={asset}
        workTemplateRequired={requireWorkType}
      />
      <DeleteModal
        open={openDeleteModal}
        content={`Are you sure you want to delete ${asset.name}?`}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default HeaderControls;
